/**
 * CiActivityPanel — panel derecho de una ficha: actividad y comentarios.
 *
 * QUÉ RESUELVE
 * ---------------------------------------------------------------------------
 * Cada ficha tenía su propia copia del «chatter» de la derecha, con tres
 * problemas repetidos en todas:
 *
 *  1. **Las notas no se guardaban.** `LocationForm` las añadía a un array del
 *     componente (`setNotes`) sin llamar a la API: al cerrar la ficha
 *     desaparecían. `ServerForm` sí llamaba, pero leía mal la respuesta
 *     (`data.map` sobre `{notes: [...]}`), así que al volver a abrir la ficha no
 *     aparecía ninguna: parecía que no se habían grabado.
 *  2. **No había historial.** Se guardaba el cambio y no quedaba constancia de
 *     quién lo hizo: `audit_logs` solo lo escribían los servidores.
 *  3. Cada una se inventaba una entrada falsa («Sistema · Ficha abierta en modo
 *     lectura») para que el panel no se viera vacío.
 *
 * Este componente es el único sitio donde vive ese panel, ya con datos de
 * verdad:
 *   · **Actividad**: creación, modificaciones y borrado del elemento, con el
 *     campo que cambió, el valor anterior y el nuevo, y quién lo hizo
 *     (`GET /api/audit/history`, escrito por `app/audit_trail.py`).
 *   · **Comentarios**: notas con autor y fecha, guardadas de verdad
 *     (`/api/ci-notes`).
 *
 * SE PUEDE OCULTAR
 * ---------------------------------------------------------------------------
 * El botón «»» del encabezado lo pliega a una pestaña fina. La preferencia se
 * guarda en el navegador, así que quien trabaja con el panel cerrado no tiene
 * que cerrarlo en cada ficha. Mientras no hay elemento guardado (un alta
 * todavía sin id) no hay nada que historiar ni a qué colgar notas: se dice.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { hace } from '../../utils/relativeTime';

const CLAVE_VISIBLE = 'cmdb_ci_activity_panel';

/** «user@dominio» → «user»; «Marta Ruiz» se queda igual. Las siglas salen de ahí. */
function iniciales(autor) {
  const texto = String(autor || '').trim();
  if (!texto) return '¿?';
  const base = texto.includes('@') ? texto.split('@')[0] : texto;
  const partes = base.split(/[\s._-]+/).filter(Boolean);
  if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

const TONO_ACCION = {
  create: 'list-badge--on',
  update: 'list-badge--warn',
  delete: 'list-badge--off',
  clone: 'list-badge--accent',
};

export default function CiActivityPanel({
  companyId,
  ciType,
  ciId = null,
  tipoLegible = '',
  onClose,
}) {
  const [visible, setVisible] = useState(() => {
    try {
      if (localStorage.getItem(CLAVE_VISIBLE) === 'oculto') return false;
      // En un móvil el panel (320 px) dejaba el formulario en una tira: arranca
      // plegado y se abre desde la pestaña «Actividad» cuando se necesita.
      if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
        return false;
      }
      return true;
    } catch {
      return true;
    }
  });
  const [bloque, setBloque] = useState('actividad');

  const [actividad, setActividad] = useState([]);
  const [cargandoActividad, setCargandoActividad] = useState(false);
  const [error, setError] = useState('');

  const [notas, setNotas] = useState([]);
  const [cargandoNotas, setCargandoNotas] = useState(false);
  const [texto, setTexto] = useState('');
  const [guardandoNota, setGuardandoNota] = useState(false);

  const guardarVisible = (valor) => {
    setVisible(valor);
    try { localStorage.setItem(CLAVE_VISIBLE, valor ? 'visible' : 'oculto'); } catch { /* modo privado */ }
  };

  const cargarActividad = useCallback(async () => {
    if (!companyId || !ciId || !ciType) { setActividad([]); return; }
    setCargandoActividad(true);
    try {
      const res = await api.getCiHistory(ciType, ciId, companyId);
      setActividad(res?.data || []);
    } catch {
      // El panel no debe romper la ficha: si el historial no carga, se dice.
      setActividad([]);
    } finally {
      setCargandoActividad(false);
    }
  }, [companyId, ciId, ciType]);

  const cargarNotas = useCallback(async () => {
    if (!companyId || !ciId || !ciType) { setNotas([]); return; }
    setCargandoNotas(true);
    try {
      const res = await api.getCiNotes(ciType, ciId, companyId);
      setNotas(res?.notes || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las notas');
      setNotas([]);
    } finally {
      setCargandoNotas(false);
    }
  }, [companyId, ciId, ciType]);

  useEffect(() => { cargarActividad(); }, [cargarActividad]);
  useEffect(() => { cargarNotas(); }, [cargarNotas]);

  const añadirNota = async () => {
    const limpio = texto.trim();
    if (!limpio || guardandoNota || !ciId) return;
    setGuardandoNota(true);
    setError('');
    try {
      await api.createCiNote({ ci_type: ciType, ci_id: ciId, text: limpio }, companyId);
      setTexto('');
      await cargarNotas();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la nota');
    } finally {
      setGuardandoNota(false);
    }
  };

  const borrarNota = async (notaId) => {
    try {
      await api.deleteCiNote(notaId, companyId);
      await cargarNotas();
    } catch (err) {
      setError(err.message || 'No se pudo borrar la nota');
    }
  };

  if (!visible) {
    return (
      <button
        type="button"
        className="dv-activity-rail"
        onClick={() => guardarVisible(true)}
        title="Mostrar el panel de actividad y comentarios"
      >
        <ChevronLeftIcon style={{ width: 16, height: 16 }} />
        <span>Actividad</span>
      </button>
    );
  }

  const sinElemento = !ciId;

  return (
    <aside className="dv-sidebar dv-activity">
      <div className="dv-sidebar-header">
        <button
          type="button"
          className={`dv-chatter-btn${bloque === 'actividad' ? ' active' : ''}`}
          onClick={() => setBloque('actividad')}
          title="Qué se ha hecho en este elemento y quién lo hizo"
        >
          <ClockIcon style={{ width: 13, height: 13, verticalAlign: '-2px', marginRight: 4 }} />
          Actividad
        </button>
        <button
          type="button"
          className={`dv-chatter-btn${bloque === 'notas' ? ' active' : ''}`}
          onClick={() => setBloque('notas')}
          title="Notas del equipo sobre este elemento"
        >
          <ChatBubbleLeftRightIcon style={{ width: 13, height: 13, verticalAlign: '-2px', marginRight: 4 }} />
          Comentarios
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
          <button
            type="button"
            className="dv-activity-icon-btn"
            onClick={() => (bloque === 'notas' ? cargarNotas() : cargarActividad())}
            title="Volver a cargar"
          >
            <ArrowPathIcon style={{ width: 15, height: 15 }} />
          </button>
          <button
            type="button"
            className="dv-activity-icon-btn"
            onClick={() => (onClose ? onClose() : guardarVisible(false))}
            title="Ocultar el panel"
          >
            <ChevronRightIcon style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </div>

      <div className="dv-chatter-body">
        {error && <div className="list-badge list-badge--off" style={{ display: 'block' }}>{error}</div>}

        {sinElemento ? (
          <p className="list-hint" style={{ margin: 0 }}>
            Guarda {tipoLegible || 'el elemento'} primero: hasta que exista no hay actividad que mostrar
            ni a qué colgar comentarios.
          </p>
        ) : bloque === 'actividad' ? (
          <>
            {cargandoActividad && (
              <div className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="spinner-border spinner-border-sm" aria-hidden="true" /> Cargando la actividad…
              </div>
            )}
            {!cargandoActividad && actividad.length === 0 && (
              <p className="list-hint" style={{ margin: 0 }}>
                Todavía no hay operaciones registradas en este elemento. A partir de ahora se anotan aquí
                las altas, las modificaciones y el borrado, con lo que cambió y quién lo hizo.
              </p>
            )}
            {actividad.map((e) => (
              <div key={e.id} className="dv-chatter-entry">
                <div className="dv-avatar">{iniciales(e.autor)}</div>
                <div className="dv-chatter-meta" style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span className={`list-badge ${TONO_ACCION[e.action] || 'list-badge--muted'}`}>{e.accion}</span>
                    <span className="dv-chatter-when">{e.at ? hace(e.at) : ''}</span>
                  </div>
                  <span className="dv-chatter-text">{e.description}</span>
                  {e.cambios && Object.keys(e.cambios).length > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {Object.entries(e.cambios).map(([campo, info]) => (
                        <span key={campo} className="list-hint">
                          <strong>{campo}</strong>:{' '}
                          {info.antes === null || info.antes === '' ? 'vacío' : String(info.antes)}
                          {' → '}
                          {info.despues === null || info.despues === '' ? 'vacío' : String(info.despues)}
                        </span>
                      ))}
                    </div>
                  )}
                  <span className="list-hint">por {e.autor}</span>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            <textarea
              className="dv-note-input"
              rows={3}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Escribe un comentario… (Ctrl+Enter para guardarlo)"
              onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) añadirNota(); }}
            />
            <button
              type="button"
              className="dv-btn dv-btn-primary"
              style={{ alignSelf: 'flex-end' }}
              onClick={añadirNota}
              disabled={guardandoNota || !texto.trim()}
            >
              {guardandoNota ? 'Guardando…' : 'Añadir comentario'}
            </button>

            {cargandoNotas && (
              <div className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="spinner-border spinner-border-sm" aria-hidden="true" /> Cargando comentarios…
              </div>
            )}
            {!cargandoNotas && notas.length === 0 && (
              <p className="list-hint" style={{ margin: 0 }}>Sin comentarios todavía.</p>
            )}
            {notas.map((n) => {
              // Las notas antiguas guardaron el id del IdP; el backend manda el
              // nombre resuelto en `created_by_name`.
              const autor = n.created_by_name || n.created_by;
              return (
                <div key={n.id} className="dv-chatter-entry">
                  <div className="dv-avatar">{iniciales(autor)}</div>
                  <div className="dv-chatter-meta" style={{ flex: 1, minWidth: 0 }}>
                    <span className="dv-chatter-who">{autor || 'Sin registrar'}</span>
                    <span className="dv-chatter-when">
                      {n.created_at ? `${new Date(n.created_at).toLocaleString()} · ${hace(n.created_at)}` : ''}
                    </span>
                    <span className="dv-chatter-text">{n.text}</span>
                  </div>
                  <button
                    type="button"
                    className="dv-activity-icon-btn"
                    onClick={() => borrarNota(n.id)}
                    title="Borrar el comentario"
                  >
                    <TrashIcon style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              );
            })}
          </>
        )}
      </div>
    </aside>
  );
}
